import { Box, type BoxProps } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

export type Base64ImageProps = Omit<BoxProps<'img'>, 'component' | 'src'> & {
  src?: string | null;
  onImageLoad?: () => void;
};

const isDataUrl = (value: string): boolean => value.startsWith('data:');

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image data.'));
    reader.onloadend = () =>
      resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(blob);
  });

export const Base64Image = ({
  src,
  onImageLoad,
  ...rest
}: Base64ImageProps): React.JSX.Element | null => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    const loadImage = async (): Promise<void> => {
      if (!src) {
        setDataUrl(null);
        return;
      }

      if (isDataUrl(src)) {
        setDataUrl(src);
        return;
      }

      try {
        const response = await fetch(src);
        if (!response.ok) {
          if (isActive) {
            setDataUrl(null);
            onImageLoad?.();
          }

          console.error('Failed to load image:', response.status, response.statusText);
          return;
        }

        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);

        if (isActive) {
          setDataUrl(dataUrl);
        }
      } catch (error) {
        if (isActive) {
          setDataUrl(null);
          onImageLoad?.();
        }

        console.error('Failed to load image:', error);
      }
    };

    void loadImage();

    return () => {
      isActive = false;
    };
  }, [onImageLoad, src]);

  const imgSrc = useMemo(() => dataUrl ?? undefined, [dataUrl]);

  if (!imgSrc) {
    return null;
  }

  return <Box component="img" src={imgSrc} onLoad={onImageLoad} {...rest} />;
};
